<script setup lang="ts">
  import { computed, ref, watch } from "vue";
  import { watchDebounced } from "@vueuse/core";
  import useMyFetch from "@stores/useMyFetch";
  import { Button } from "@/components/ui/button";
  import { Input } from "@/components/ui/input";
  import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
  import { EmptyState } from "@/components/episto-ui/empty-state";
  import { Upload, Loader2, X, Search } from "lucide-vue-next";

  interface UnsplashResult {
    id: string;
    description: string | null;
    photographer: string | null;
    photographer_url: string | null;
    width: number;
    height: number;
    thumb_url: string;
    url: string;
    download_location: string | null;
  }

  const UNSPLASH_ATTRIBUTION_URL = "https://unsplash.com/?utm_source=episto&utm_medium=referral";

  interface ImageBinding {
    value?: string | null;
    photographer?: string | null;
    photographer_url?: string | null;
  }

  const props = withDefaults(defineProps<{ binding?: ImageBinding }>(), { binding: () => ({}) });

  const emit = defineEmits<{ "update:image": [image: ImageBinding] }>();

  // Mirrors the image extensions allowed by Storages::Validations::FileUploads (api).
  const ACCEPTED_EXTENSIONS = ".jpg,.jpeg,.png,.webp,.avif,.gif";

  const isUploading = ref(false);
  const uploadError = ref<string | null>(null);
  const fileInput = ref<HTMLInputElement | null>(null);

  const searchOpen = ref(false);
  const searchQuery = ref("");
  const searchResults = ref<UnsplashResult[]>([]);
  const isSearching = ref(false);
  const searchError = ref<string | null>(null);

  const imageUrl = computed(() => props.binding.value ?? null);
  const photographer = computed(() => props.binding.photographer ?? null);
  const photographerUrl = computed(() => props.binding.photographer_url ?? null);

  const handleFileSelect = async (event: Event) => {
    const file = (event.target as HTMLInputElement).files?.[0];
    if (!file) return;

    isUploading.value = true;
    uploadError.value = null;
    try {
      const formData = new FormData();
      formData.append("file[attachment]", file);
      formData.append("file[type]", file.type);

      const { response, data } = await useMyFetch("/file_uploads/")
        .post(formData, null)
        .json<{ url?: string; error?: string }>();
      if (!response.value?.ok) throw new Error(data.value?.error || "Upload failed");

      emit("update:image", { value: data.value?.url });
    } catch (err) {
      uploadError.value = err instanceof Error ? err.message : "Upload failed";
    } finally {
      isUploading.value = false;
      if (fileInput.value) fileInput.value.value = "";
    }
  };

  const removeImage = () => {
    uploadError.value = null;
    emit("update:image", { value: null });
  };

  const triggerFileInput = () => {
    fileInput.value?.click();
  };

  const runSearch = async () => {
    const query = searchQuery.value.trim();
    if (!query) {
      searchResults.value = [];
      return;
    }

    isSearching.value = true;
    searchError.value = null;

    const { response, data } = await useMyFetch(`/unsplash/search?query=${encodeURIComponent(query)}`)
      .get()
      .json<{ results: UnsplashResult[] }>();

    if (response.value.ok) {
      searchResults.value = data.value?.results ?? [];
    } else {
      searchResults.value = [];
      searchError.value = "Search failed. Please try again.";
    }

    isSearching.value = false;
  };

  watchDebounced(searchQuery, runSearch, { debounce: 400 });

  watch(searchOpen, (open) => {
    if (!open) {
      searchQuery.value = "";
      searchResults.value = [];
      searchError.value = null;
    }
  });

  // The Unsplash API Guidelines require reporting a photo as downloaded once the user picks it.
  const trackDownload = (downloadLocation: string | null) => {
    if (!downloadLocation) return;

    useMyFetch("/unsplash/track_download").post({ download_location: downloadLocation }).json();
  };

  const selectResult = (result: UnsplashResult) => {
    trackDownload(result.download_location);
    emit("update:image", {
      value: result.url,
      photographer: result.photographer,
      photographer_url: result.photographer_url,
    });
    searchOpen.value = false;
  };
</script>

<template>
  <div class="space-y-3">
    <p
      v-if="uploadError"
      class="text-sm text-destructive">
      {{ uploadError }}
    </p>

    <!-- Preview -->
    <div v-if="imageUrl">
      <div class="relative inline-block">
        <img
          :src="imageUrl"
          alt="Uploaded image"
          class="max-h-16 rounded border object-cover" />
        <button
          type="button"
          class="absolute -top-2 -right-2 bg-status-error text-white rounded-full p-1 hover:bg-status-error"
          @click="removeImage">
          <X class="h-3 w-3" />
        </button>
      </div>
      <p
        v-if="photographer"
        class="mt-1 text-xs text-muted-foreground">
        Photo by
        <a
          :href="photographerUrl ?? UNSPLASH_ATTRIBUTION_URL"
          target="_blank"
          rel="noopener noreferrer"
          class="underline hover:text-foreground">{{ photographer }}</a>
        on
        <a
          :href="UNSPLASH_ATTRIBUTION_URL"
          target="_blank"
          rel="noopener noreferrer"
          class="underline hover:text-foreground">Unsplash</a>
      </p>
    </div>

    <!-- Actions -->
    <div class="flex items-center gap-2">
      <input
        ref="fileInput"
        type="file"
        :accept="ACCEPTED_EXTENSIONS"
        class="hidden"
        @change="handleFileSelect" />
      <Button
        type="button"
        variant="outline"
        size="sm"
        :disabled="isUploading"
        @click="triggerFileInput">
        <Loader2
          v-if="isUploading"
          class="mr-2 h-4 w-4 animate-spin" />
        <Upload
          v-else
          class="mr-2 h-4 w-4" />
        {{ isUploading ? "Uploading..." : imageUrl ? "Change image" : "Upload image" }}
      </Button>

      <Popover v-model:open="searchOpen">
        <PopoverTrigger as-child>
          <Button
            type="button"
            variant="outline"
            size="sm">
            <Search class="mr-2 h-4 w-4" />
            Search Unsplash
          </Button>
        </PopoverTrigger>
        <PopoverContent
          class="w-80 p-3 space-y-3"
          align="start">
          <div class="relative">
            <Input
              v-model="searchQuery"
              placeholder="Search photos..."
              class="h-9 pr-8" />
            <Loader2
              v-if="isSearching"
              class="absolute right-2 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin text-muted-foreground" />
          </div>

          <p
            v-if="searchError"
            class="text-sm text-destructive">
            {{ searchError }}
          </p>

          <div
            v-else-if="searchResults.length"
            class="grid grid-cols-3 gap-2 max-h-64 overflow-y-auto">
            <button
              v-for="result in searchResults"
              :key="result.id"
              type="button"
              class="aspect-square overflow-hidden rounded border hover:ring-2 hover:ring-ring"
              :title="result.description ?? undefined"
              @click="selectResult(result)">
              <img
                :src="result.thumb_url"
                :alt="result.description ?? 'Unsplash photo'"
                class="h-full w-full object-cover"
                loading="lazy" />
            </button>
          </div>

          <EmptyState
            v-else-if="searchQuery.trim() && !isSearching"
            class="py-2"
            title="No results" />
        </PopoverContent>
      </Popover>
    </div>
  </div>
</template>
